"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Ensemble = void 0;
const TaskService_1 = require("./services/TaskService");
const AgentService_1 = require("./services/AgentService");
const ServiceRegistryService_1 = require("./services/ServiceRegistryService");
const typechain_1 = require("../typechain");
class Ensemble {
    constructor(taskService, agentService, serviceRegistryService) {
        this.taskService = taskService;
        this.agentService = agentService;
        this.serviceRegistryService = serviceRegistryService;
    }
    static create(config, signer, ipfsSDK) {
        const serviceRegistry = typechain_1.ServiceRegistry__factory.connect(config.serviceRegistryAddress, signer);
        const agentRegistry = typechain_1.AgentsRegistry__factory.connect(config.agentRegistryAddress, signer);
        const taskRegistry = typechain_1.TaskRegistry__factory.connect(config.taskRegistryAddress, signer);
        const serviceRegistryService = new ServiceRegistryService_1.ServiceRegistryService(serviceRegistry);
        const agentService = new AgentService_1.AgentService(agentRegistry, signer, ipfsSDK, config.subgraphUrl);
        const taskService = new TaskService_1.TaskService(taskRegistry, agentService);
        return new Ensemble(taskService, agentService, serviceRegistryService);
    }
    /**
     * Starts the Ensemble subscription to the task service.
     */
    async start() {
        this.taskService.subscribe();
    }
    async stop() {
        this.taskService.unsubscribe();
    }
    /**
     * Creates a new task.
     * @param {TaskCreationParams} params - The parameters for task creation.
     * @returns {Promise<TaskData>} A promise that resolves to the task ID.
     */
    async createTask(params) {
        return this.taskService.createTask(params);
    }
    /**
     * Gets data for a specific task.
     * @param {string} taskId - The ID of the task.
     * @returns {Promise<TaskData>} A promise that resolves to the task data.
     */
    async getTaskData(taskId) {
        return this.taskService.getTaskData(taskId);
    }
    /**
     * Gets tasks by issuer.
     * @param {string} issuer - The owner of the tasks.
     * @returns {Promise<bigint[]>} A promise that resolves to the task IDs.
     */
    async getTasksByIssuer(issuer) {
        return this.taskService.getTasksByIssuer(issuer);
    }
    /**
     * Completes a task.
     * @param {string} taskId - The ID of the task.
     * @param {string} result - The result of the task.
     * @returns {Promise<void>} A promise that resolves when the task is completed.
     */
    async completeTask(taskId, result) {
        return this.taskService.completeTask(taskId, result);
    }
    /**
     * Assigns a rating to a task.
     * @param {string} taskId - The ID of the task.
     * @param {number} rating - The rating.
     * @returns {Promise<void>} A promise that resolves when the task is assigned.
     */
    async rateTask(taskId, rating) {
        return this.taskService.rateTask(taskId, rating);
    }
    /**
     * Cancels a task.
     * @param {string} taskId - The ID of the task.
     * @returns {Promise<void>} A promise that resolves when the task is canceled.
     */
    async cancelTask(taskId) {
        return this.taskService.cancelTask(taskId);
    }
    /**
     * Registers a new agent with service.
     * @param {string} address - The address of the agent.
     * @param {AgentMetadata} metadata - The metadata of the agent.
     * @returns {Promise<boolean>} A promise that resolves to the agent registration status.
     */
    async registerAgent(address, metadata) {
        return this.agentService.registerAgent(address, metadata);
    }
    /**
     * Registers a new agent with service.
     * @param {string} address - The address of the agent.
     * @param {AgentMetadata} metadata - The metadata of the agent.
     * @param {string} serviceName - The name of the service.
     * @param {number} servicePrice - The price of the service.
     * @param {string} tokenAddress - The token address for payment.
     * @returns {Promise<boolean>} A promise that resolves to the agent registration status.
     */
    async registerAgentWithService(address, metadata, serviceName, servicePrice, tokenAddress = "0x0000000000000000000000000000000000000000" // Default to zero address for ETH
    ) {
        return this.agentService.registerAgentWithService(address, metadata, serviceName, servicePrice, tokenAddress);
    }
    /**
     * Gets the address of the agent.
     * @returns {Promise<string>} A promise that resolves to the agent address.
     */
    async getWalletAddress() {
        return this.agentService.getAgentAddress();
    }
    /**
     * Gets data for a specific agent.
     * @param {string} agentAddress - The address of the agent.
     * @returns {Promise<AgentData>} A promise that resolves to the agent data.
     */
    async getAgent(agentId) {
        return this.agentService.getAgent(agentId);
    }
    /**
     * Updates the metadata of an existing agent.
     * @param {string} agentAddress - The address of the agent to update.
     * @param {AgentMetadata} metadata - The new metadata for the agent.
     * @returns {Promise<boolean>} A promise that resolves to true if the update was successful.
     */
    async updateAgentMetadata(agentAddress, metadata) {
        return this.agentService.updateAgentMetadata(agentAddress, metadata);
    }
    /**
     * Gets all agents owned by a specific address.
     * @param {string} ownerAddress - The address of the owner.
     * @returns {Promise<AgentData[]>} A promise that resolves to an array of agent data.
     */
    async getAgentsByOwner(ownerAddress) {
        return this.agentService.getAgentsByOwner(ownerAddress);
    }
    /**
     * Sets a listener for new tasks.
     * @param {function} listener - The listener function.
     * @returns {Promise<void>} A promise that resolves when the listener is set.
     */
    async setOnNewTaskListener(listener) {
        return this.taskService.setOnNewTaskListener(listener);
    }
    /**
     * Registers a new service.
     * @param {Service} service - The service to register.
     * @returns {Promise<boolean>} A promise that resolves to a boolean indicating if the service is registered.
     */
    async registerService(service) {
        return this.serviceRegistryService.registerService(service);
    }
    /**
     * Gets a service by name.
     * @param {string} name - The name of the service.
     * @returns {Promise<Service>} A promise that resolves to the service.
     */
    async getService(name) {
        return this.serviceRegistryService.getService(name);
    }
    /**
     * Add a proposal for an agent.
     * @param {string} agentAddress The address of the agent.
     * @param {string} serviceName The name of the service.
     * @param {number} servicePrice The price of the service.
     * @param {string} tokenAddress The token address for payment.
     * @returns {Promise<boolean>} A promise that resolves to a boolean indicating if the proposal was added.
     */
    async addProposal(agentAddress, serviceName, servicePrice, tokenAddress) {
        return this.agentService.addProposal(agentAddress, serviceName, servicePrice, tokenAddress);
    }
}
exports.Ensemble = Ensemble;
exports.default = Ensemble;
